import { Fragment, lazy, Suspense, useMemo, useState } from "react";
const SurvrepGoruntuleyici = lazy(() => import("@/components/survrep").then((m) => ({ default: m.SurvrepGoruntuleyici })));
import { Link } from "@tanstack/react-router";
import {
  KALEM_DURUMLARI,
  type DosyaBolumu,
  type DosyaSatiri,
  type IzlenebilirlikSatiri,
  type IzlenebilirlikTakilan,
  type KalemGrubu,
  type OemKaydi,
  type ParcaDurumu,
  type RiserDosyasiYaniti,
  type RiserOzetleriYaniti,
  type SayfaAraligi,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { useVeri, tarih } from "@/lib/veri";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { Band, Bos, Etiket, Hata, Kutu, Sayfa } from "@/components/parcalar";
import { belgeLinki, sayfaEtiketi } from "@/components/pdf-sayfa";
import {
  BOS_DIZIN,
  DURUM_TONU,
  acilis,
  belgeSayaci,
  blokBasligi,
  bolumSayaci,
  dosyaDizini,
  dosyaYolu,
  durumAdi,
  grupBasligi,
  grupBosMetni,
  grupNotu,
  hatAdi,
  hatKaresi,
  listeBolumBasligi,
  listeOzeti,
  listeParcalari,
  listeYolu,
  oemMetni,
  ornekVericiRn,
  ozetMetni,
  parcaMetni,
  parcaTonu,
  riserSuz,
  satirAdi,
  satirAltMetni,
  satirCipleri,
  satirEkrani,
  satirSonKolon,
  simdiTakiliCipi,
  snMetni,
  takilanCipleri,
  takilanHucresi,
  type Acilis,
  type Bicim,
  type Dizin,
} from "@/lib/riser-belgeleri";

/**
 * D-058: Riser Documentation. Bir riserin bütün belgeleri, Oil States onarım dosyasının (şablon OSIUK-Q-XX-0001) beş bölümü ve 30
 * kalemiyle aynı sırada. Sayfa salt okunurdur: belge burada yüklenmez, kaldırılmaz; tek düğme "Export list (Excel)".
 * İki sekme yerel durumdur: "Documents on file" (yüklü belgeler) ve "Recertification document list" (30 kalemlik liste).
 * Sunucu hazır cümle göndermez; metinler lib/riser-belgeleri.ts içinde kurulur. Sınıflar onaylı statik sayfalardan birebir.
 */

export type Sekme = "belgeler" | "liste";
const SEKMELER: { kod: Sekme; ad: string }[] = [
  { kod: "belgeler", ad: cevir("Documents on file") },
  { kod: "liste", ad: cevir("Recertification document list") },
];

/** Uygulama içi sayfa görüntüleyicide açılan belge (OEM kitabının sayfa aralığı). */
type Goruntulenen = { dokumanId: string; ad: string; sayfa: number };
/**
 * Alt bileşenlerin ortak girdisi: yanıtın dizini, görüntüleyiciyi açan işlev ve riserin iş emri. `yukleyenGizli` ve `bantGizli`
 * yalnız Dashboard'dan gelir (D-071); Riser Documentation ekranında ikisi de kapalıdır.
 */
type Ortam = { dizin: Dizin; goster: (g: Goruntulenen) => void; isEmri: string | null; yukleyenGizli: boolean; bantGizli: boolean };

const BICIM: Bicim = { tarih: (iso) => tarih(iso), sayfa: (a) => sayfaEtiketi(a) };

/** Hap düğme (batch süzgeci, riser hapı, sekme): Komuta'daki batch süzgeciyle aynı sınıflar. Dashboard'un seçici kartı da bunları kullanır (D-071). */
export const HAP = "h-kontrol-kucuk shrink-0 rounded-full border px-3 text-sm font-medium transition";
export const HAP_SECILI = "border-navy bg-navy text-white dark:text-[#0a141c]";
export const HAP_DIGER = "border-line bg-surface text-ink-2 hover:border-line-strong";
/** Kalem (ya da blok) başlığı, belge satırı ve "No document." satırı: riser-documentation.html ile birebir. */
const KALEM_BASLIGI = "mt-3 flex flex-wrap items-center gap-2 border-b border-line pb-1";
const BELGE_SATIRI = "flex flex-wrap items-center gap-2 border-b border-line py-2 text-sm";
const BOS_SATIR = "border-b border-line py-2 text-sm text-ink-3";
const TABLO_BASLIGI = "py-1 pr-3 font-semibold";
const TABLO_HUCRESI = "py-2 pr-3";

/** Seçicinin süzgeci iki rota arasında (riser seçili / seçili değil) korunur: riser seçilince arama kutusu boşalmasın. */
const suzgec: { arama: string; batch: string | null } = { arama: "", batch: null };

export function RiserBelgeleri({ rn }: { rn?: string }) {
  const [sekme, setSekme] = useState<Sekme>("belgeler");
  return (
    <Sayfa
      ust={cevir("5.6 · Riser file")}
      baslik={cevir("Riser Documentation")}
      alt={<>{cevir("Every document that belongs to one riser, in the order of the Oil States overhaul record book (template OSIUK-Q-XX-0001: five sections). Documents are uploaded on their own screens; this page only collects and shows them.")} <b>{cevir("Documents on file")}</b> {cevir("lists what is uploaded today;")} <b>{cevir("Recertification document list")}</b> {cevir("is the index of the file that goes to DNV.")}</>}
    >
      <Secici rn={rn} />
      {rn ? <RiserDosyasi key={rn} rn={rn} sekme={sekme} setSekme={setSekme} /> : <Bos>{cevir("Select a riser to see its documents.")}</Bos>}
    </Sayfa>
  );
}

/** Riser picker: arama kutusu, batch hapları ve seçili batch'in bütün riserleri (RN sırasıyla, belge sayısıyla; belgesi olmayan soluk). */
function Secici({ rn }: { rn?: string }) {
  const { veri, hata } = useVeri<RiserOzetleriYaniti>("/riser-belgeleri");
  const [arama, setArama] = useState(suzgec.arama);
  const [batch, setBatch] = useState<string | null>(suzgec.batch);
  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const riserler = riserSuz(veri.riserler, arama, batch);
  return (
    <Kutu padded={false}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <input
          className="h-kontrol-kucuk rounded-md border border-line bg-surface px-3 text-sm"
          placeholder={cevir("Search riser no. or Assembly SN...")}
          aria-label={cevir("Search riser no. or Assembly SN...")}
          value={arama}
          onChange={(e) => {
            suzgec.arama = e.target.value;
            setArama(e.target.value);
          }}
        />
        {[null, ...veri.batchler.map((b) => b.no)].map((b) => (
          <button
            key={b ?? ""}
            type="button"
            aria-pressed={batch === b}
            onClick={() => {
              suzgec.batch = b;
              setBatch(b);
            }}
            className={cn(HAP, batch === b ? HAP_SECILI : HAP_DIGER)}
          >
            {b ?? cevir("All")}
            {b !== null && <span className="ml-1 opacity-70 tnum">{veri.batchler.find((x) => x.no === b)?.riser}</span>}
          </button>
        ))}
        <span className="ml-auto flex flex-wrap gap-1">
          {riserler.map((r) => (
            <Link
              key={r.rn}
              to="/riser-documentation/$rn"
              params={{ rn: r.rn }}
              aria-current={r.rn === rn ? "page" : undefined}
              className={cn(HAP, "inline-flex items-center", r.rn === rn ? HAP_SECILI : HAP_DIGER, r.rn !== rn && r.belgeSayisi === 0 && "opacity-55")}
            >
              {r.rn}
              <span className="ml-1 opacity-70 tnum">{r.belgeSayisi}</span>
            </Link>
          ))}
          {!riserler.length && <span className="text-sm text-ink-3">{cevir("No riser in this filter.")}</span>}
        </span>
      </div>
    </Kutu>
  );
}

/**
 * Seçili riserin dosyası. `key={rn}` ile kurulur: riser değişince önceki riserin verisi ekranda kalmaz.
 *
 * D-071: Dashboard aynı bileşeni sayfanın en altında çizer ve üç isteğe bağlı seçeneği açar. Üçünün de varsayılanı kapalıdır: seçenek
 * verilmeyen çağrı (Riser Documentation ekranı) eskisiyle aynı çizer.
 *  - `yukleyenGizli`: belge satırının son hücresi boş kalır (yükleyen, release veren, yükleme günü); fit-up kaydında "released" sözü
 *    ve "Declarations in place of documents" satırı günsüz yazılır, beyanı yapanın adı da yazılmaz.
 *  - `bantGizli`: iki sekmenin sonundaki bilgi bantları çizilmez.
 *  - `hatKareleriGizli`: başlıktaki beş hat karesi çizilmez (kareler Finishing satırının hat durum kodunu gösterir; Dashboard'un
 *    "Lines" kareleri başka ölçüyle çizilir, aynı sayfada iki ölçü yan yana gelmesin).
 * Gizleme yalnız çizimdedir: uç yükleyen adlarını ve günlerini göndermeye devam eder.
 */
export function RiserDosyasi({
  rn,
  sekme,
  setSekme,
  yukleyenGizli = false,
  bantGizli = false,
  hatKareleriGizli = false,
}: {
  rn: string;
  sekme: Sekme;
  setSekme: (s: Sekme) => void;
  yukleyenGizli?: boolean;
  bantGizli?: boolean;
  hatKareleriGizli?: boolean;
}) {
  const { veri, hata } = useVeri<RiserDosyasiYaniti>(dosyaYolu(rn));
  const [pdf, setPdf] = useState<Goruntulenen | null>(null);
  const dizin = useMemo(() => (veri ? dosyaDizini(veri) : BOS_DIZIN), [veri]);
  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const o: Ortam = { dizin, goster: setPdf, isEmri: veri.riser.isEmri, yukleyenGizli, bantGizli };
  return (
    <>
      <RiserBasligi v={veri} sekme={sekme} setSekme={setSekme} hatKareleriGizli={hatKareleriGizli} />
      {sekme === "belgeler" ? (
        <>
          <Izlenebilirlik satirlar={veri.izlenebilirlik} montajKaydi={veri.montajKaydi ?? null} rn={veri.riser.rn} o={o} />
          {veri.bolumler.map((b) => <Bolum key={b.no} b={b} o={o} />)}
          <Kutu baslik={cevir("Reference — not part of the Oil States file")} aksiyon={belgeSayaci(veri.referans.length)}>
            {veri.referans.length ? veri.referans.map((s) => <BelgeSatiri key={s.id} s={s} o={o} hatGoster />) : <div className={BOS_SATIR}>{cevir("No document.")}</div>}
          </Kutu>
          {!bantGizli && <Band tip="bilgi">{cevir("This page is read-only. Each document is uploaded by the owner of its step on that step's screen; the last column names the screen. Work order and project documents are uploaded once and appear on every riser they apply to.")}</Band>}
        </>
      ) : (
        <BelgeListesi v={veri} o={o} />
      )}
      {pdf && (
        <Suspense fallback={null}>
          <SurvrepGoruntuleyici dokumanId={pdf.dokumanId} ad={pdf.ad} sayfa={pdf.sayfa} onKapat={() => setPdf(null)} />
        </Suspense>
      )}
    </>
  );
}

/** Riser header: RN, beş hat karesi (`hatKareleriGizli` değilse), çipler, sağda özet; alt şeritte iki sekme ve kapsam çiplerinin açıklaması. */
function RiserBasligi({ v, sekme, setSekme, hatKareleriGizli }: { v: RiserDosyasiYaniti; sekme: Sekme; setSekme: (s: Sekme) => void; hatKareleriGizli: boolean }) {
  const r = v.riser;
  return (
    <Kutu padded={false}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <b className="min-w-[52px] font-mono text-xl">{r.rn}</b>
        {!hatKareleriGizli && (
          <span className="inline-flex gap-1">
            {r.hatlar.map((h) => <span key={h.hat} title={h.hat} className={cn("inline-block h-3.5 w-3.5 rounded-[3px]", hatKaresi(h.kod))} />)}
          </span>
        )}
        {r.batchNo && <Etiket>{`${cevir("Batch")} ${r.batchNo}`}</Etiket>}
        {r.isEmri && <Etiket>{`${cevir("Work order")} ${r.isEmri}`}</Etiket>}
        {r.assemblySn && <Etiket>{`${cevir("Assembly SN")} ${r.assemblySn}`}</Etiket>}
        {r.overhaul && <Etiket tone="info">{cevir("Scope of work: Overhauled")}</Etiket>}
        <span className="ml-auto text-sm text-ink-2">{ozetMetni(v.belgeSayisi, v.sayac)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-2">
        {SEKMELER.map((s) => (
          <button key={s.kod} type="button" aria-pressed={sekme === s.kod} onClick={() => setSekme(s.kod)} className={cn(HAP, sekme === s.kod ? HAP_SECILI : HAP_DIGER)}>
            {s.ad}
          </button>
        ))}
        <span className="ml-auto flex flex-wrap items-center gap-1 text-xs text-ink-3">
          {cevir("Scope:")} <Etiket>{cevir("Riser")}</Etiket>
          <Etiket>{cevir("Line")}</Etiket>
          <Etiket>{cevir("Job")}</Etiket>
          <Etiket>{cevir("Weld")}</Etiket>
          <Etiket tone="info">{cevir("via Register")}</Etiket>
          <Etiket tone="info">{cevir("Work order")}</Etiket>
          <Etiket tone="info">{cevir("Project")}</Etiket>
          <Etiket tone="warn">{cevir("From riser {0}", ornekVericiRn(v) ?? "R…")}</Etiket>
        </span>
      </div>
    </Kutu>
  );
}

/**
 * Tıklanan ad. Kayıt ve beyan uygulama içi ekrana gider; OEM kitabının sayfa aralığı uygulama içi sayfa görüntüleyicide açılır
 * (kitap büyük; Ctrl ya da Shift ile tıklanırsa PDF yeni sekmede); öteki belgeler /api/dokuman/:id ile yeni sekmede.
 * Belge kimliği yoksa bağlantısız metin.
 */
function Baglanti({ a, sayfa, metin, kalin, goster }: { a: Acilis; sayfa: SayfaAraligi | null; metin: string; kalin?: boolean; goster: (g: Goruntulenen) => void }) {
  const sinif = kalin ? "font-semibold underline" : "underline";
  const baslik = cevir("Open document");
  if (a.tip === "yok") return <span className={kalin ? "font-semibold" : undefined}>{metin}</span>;
  if (a.tip === "rota") return <Link to={a.yol} className={sinif} title={baslik}>{metin}</Link>;
  const href = belgeLinki(a.dokumanId, sayfa);
  if (a.tip === "sekme") return <a href={href} target="_blank" rel="noreferrer" className={sinif} title={baslik}>{metin}</a>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={sinif}
      title={baslik}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        goster({ dokumanId: a.dokumanId, ad: metin, sayfa: a.sayfa });
      }}
    >
      {metin}
    </a>
  );
}

/** Belge satırı: ad (bağlantı), dosya adı ve sayfa, kapsam çipleri, yüklendiği ekran, yükleyen ve tarih (`o.yukleyenGizli` ise son hücre boş). */
function BelgeSatiri({ s, o, hatGoster }: { s: DosyaSatiri; o: Ortam; hatGoster?: boolean }) {
  const alt = satirAltMetni(s, BICIM, { hatGoster, dizin: o.dizin, tarihGizli: o.yukleyenGizli, beyanEdenGizli: o.yukleyenGizli });
  return (
    <div className={BELGE_SATIRI}>
      <span className="min-w-0 flex-1">
        <Baglanti a={acilis(s)} sayfa={s.sayfa} metin={satirAdi(s)} kalin goster={o.goster} />
        <span className="block truncate text-xs text-ink-3" title={alt}>{alt}</span>
      </span>
      {satirCipleri(s).map((c) => <Etiket key={c.metin} tone={c.ton}>{c.metin}</Etiket>)}
      <span className="text-xs text-ink-2">{satirEkrani(s)}</span>
      <span className="text-xs text-ink-3">{satirSonKolon(s, BICIM, { yukleyenGizli: o.yukleyenGizli })}</span>
    </div>
  );
}

/** Kalem grubu: başlıkta şablon kalem adları (çevrilmez), altında belgeler; belge yoksa "No document." satırı. */
function Grup({ g, o }: { g: KalemGrubu; o: Ortam }) {
  const not = grupNotu(g);
  const { bos, ek } = grupBosMetni(g);
  return (
    <>
      <div className={KALEM_BASLIGI}>
        <b className="text-sm">{grupBasligi(g)}</b>
        {not && <span className="text-xs text-ink-3">{not}</span>}
      </div>
      {bos && <div className={BOS_SATIR}>{bos}</div>}
      {g.satirlar.map((s) => <BelgeSatiri key={s.id} s={s} o={o} hatGoster />)}
      {ek && <div className={BOS_SATIR}>{ek}</div>}
    </>
  );
}

/** Bölüm kartı (1 … 5). Bölüm 4 iş bloklarından ve "Used on these welds — from the Registers" satırlarından oluşur. */
function Bolum({ b, o }: { b: DosyaBolumu; o: Ortam }) {
  return (
    <Kutu baslik={`${b.no} · ${b.ad}`} aksiyon={bolumSayaci(b)}>
      {b.no === "4" ? (
        <>
          <p className="mb-1 text-sm text-ink-2">{cevir("One block per repaired or replaced component. A riser without weld repair has nothing here.")}</p>
          {b.bloklar.map((x) => (
            <Fragment key={x.isId}>
              <div className={KALEM_BASLIGI}><b className="text-sm">{blokBasligi(x)}</b></div>
              {x.satirlar.length ? x.satirlar.map((s) => <BelgeSatiri key={s.id} s={s} o={o} />) : <div className={BOS_SATIR}>{cevir("No document.")}</div>}
            </Fragment>
          ))}
          {(b.bloklar.length > 0 || b.kutukSatirlari.length > 0) && (
            <>
              <div className={KALEM_BASLIGI}><b className="text-sm">{cevir("Used on these welds — from the Registers")}</b></div>
              {b.kutukSatirlari.length ? b.kutukSatirlari.map((s) => <BelgeSatiri key={s.id} s={s} o={o} />) : <div className={BOS_SATIR}>{cevir("No document.")}</div>}
            </>
          )}
        </>
      ) : (
        b.gruplar.map((g) => <Grup key={g.kalemler.map((k) => k.no).join("+")} g={g} o={o} />)
      )}
    </Kutu>
  );
}

function ParcaCipi({ p }: { p: ParcaDurumu }) {
  const ton = parcaTonu(p);
  return ton ? <Etiket tone={ton}>{parcaMetni(p)}</Etiket> : <>{parcaMetni(p)}</>;
}

/** "OEM record" hücresi: kitabın PDF'i varsa bağlantı (sayfa aralığı hesaplandıysa o sayfada), yoksa düz metin; kitap yoksa tire. */
function OemHucresi({ oem, goster }: { oem: OemKaydi | null; goster: (g: Goruntulenen) => void }) {
  if (!oem) return <>—</>;
  const a: Acilis = !oem.dokumanId ? { tip: "yok" } : oem.sayfa ? { tip: "goruntuleyici", dokumanId: oem.dokumanId, sayfa: oem.sayfa.sayfaBas } : { tip: "sekme", dokumanId: oem.dokumanId };
  return <Baglanti a={a} sayfa={oem.sayfa} metin={oemMetni(oem, oem.sayfa ? sayfaEtiketi(oem.sayfa) : "")} goster={goster} />;
}

/**
 * D-068: "Fitted (assembly)" hücresi: montaj kaydının o hat için okuduğu kimlik (yazım, "not read" ya da "—") ve çipleri. Çipler uzun
 * olabilir; tablo 780 pikselde taşmasın diye metinleri satıra sığmazsa kırılır.
 */
function TakilanHucre({ t }: { t: IzlenebilirlikTakilan | null }) {
  const h = takilanHucresi(t);
  if (h.tur === "tire") return <>{h.metin}</>;
  const cipler = [...(h.cip ? [h.cip] : []), ...takilanCipleri(t)];
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className={h.tur === "kimlik" ? "font-mono" : h.tur === "yazim" ? "font-mono text-ink-3" : "text-ink-3"}>{h.metin}</span>
      {cipler.map((c) => <Etiket key={c.metin} tone={c.ton} className="whitespace-normal">{c.metin}</Etiket>)}
    </div>
  );
}

/**
 * "Lines and parts — traceability": beş hat; hattın seri numarası, box / pipe / pin durumu ve OEM kitabındaki yeri.
 * D-068: "Fitted (assembly)" sütunu her zaman çizilir (plan D §5.2.2; Assembly sayfasındaki Continuity kutusuyla aynı hesap;
 * saklanmaz); okunmuş montaj kaydı yoksa hücre "—". Giriş paragrafının altında Assembly sayfasına bağlantı; montaj kaydı yoksa
 * bağlantının yerinde gri "No assembly record for this riser yet.". Bu riserdan sökülen hat başka riserın montaj kaydında
 * okunuyorsa "Line SN" hücresinin altında not çipi (beyana bağlı; `simdiTakiliCipi`). "Line SN" sökümde çıkan hattı göstermeye
 * devam eder.
 */
function Izlenebilirlik({ satirlar, montajKaydi, rn, o }: { satirlar: IzlenebilirlikSatiri[]; montajKaydi: RiserDosyasiYaniti["montajKaydi"]; rn: string; o: Ortam }) {
  return (
    <Kutu baslik={cevir("Lines and parts — traceability")} aksiyon={cevir("{0} lines · from OEM MRB, Disassembly and Assembly records", satirlar.length)}>
      <p className="mb-2 text-sm text-ink-2">{cevir("Each line keeps its own serial number; a line or a part may come from another riser. Unchanged parts point to the OEM record book, replaced parts to the stock package they came from.")}</p>
      {montajKaydi !== null ? (
        <p className="mb-2 text-sm text-ink-2">
          {cevir("“Fitted (assembly)” is what the Assembly record reads for the line. The comparison and the declarations are on the riser's Assembly page.")}{" "}
          <Link to="/assembly/$rn" params={{ rn }} className="underline">{cevir("Open the Assembly page")}</Link>
        </p>
      ) : (
        <p className="mb-2 text-sm text-ink-3">{cevir("No assembly record for this riser yet.")}</p>
      )}
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[780px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-ink-3">
              <th className={TABLO_BASLIGI}>{cevir("Line")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Line SN (old ID → new ID)")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Fitted (assembly)")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Box")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Pipe")}</th>
              <th className={TABLO_BASLIGI}>{cevir("Pin")}</th>
              <th className="py-1 font-semibold">{cevir("OEM record")}</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((h, i) => (
              <tr key={h.hat} className={i < satirlar.length - 1 ? "border-b border-line" : undefined}>
                <td className={TABLO_HUCRESI}>
                  <b>{hatAdi(h.hat)}</b>
                  {/* Parçası belirlenemeyen durum hat düzeyinde bir kez yazılır, üç hücreye kopyalanmaz. */}
                  {h.hatDuzeyi.map((p) => <Etiket key={p.tur} tone={parcaTonu(p) ?? "neutral"} className="ml-1">{parcaMetni(p)}</Etiket>)}
                </td>
                <td className="py-2 pr-3 font-mono">
                  {snMetni(h)}
                  {(h.simdiTakili ?? []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1 font-plex">
                      {(h.simdiTakili ?? []).map((s) => {
                        const c = simdiTakiliCipi(s);
                        return <Etiket key={s.rn} tone={c.ton} className="whitespace-normal">{c.metin}</Etiket>;
                      })}
                    </div>
                  )}
                </td>
                <td className={TABLO_HUCRESI}><TakilanHucre t={h.takilan ?? null} /></td>
                <td className={TABLO_HUCRESI}><ParcaCipi p={h.box} /></td>
                <td className={TABLO_HUCRESI}><ParcaCipi p={h.pipe} /></td>
                <td className={TABLO_HUCRESI}><ParcaCipi p={h.pin} /></td>
                <td className="py-2"><OemHucresi oem={h.oem} goster={o.goster} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("Loose items (jewellery) are listed with quantities on the Certificate of Conformity; they carry a traceability exception and have no documents of their own.")}</p>
    </Kutu>
  );
}

/** "Recertification document list": şablonun 30 kalemi tek tabloda; sayaçlar ve "Export list (Excel)". Liste yüklü belgelerden türer. */
function BelgeListesi({ v, o }: { v: RiserDosyasiYaniti; o: Ortam }) {
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const indir = async () => {
    setHata(null);
    setMesgul(true);
    try {
      await api.indir(listeYolu(v.riser.rn));
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(false);
    }
  };
  return (
    <>
      <Hata mesaj={hata} />
      <Kutu baslik={cevir("Recertification document list")} aksiyon={listeOzeti(v.sayac)}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {KALEM_DURUMLARI.map((d) => <Etiket key={d} tone={DURUM_TONU[d]}>{`${durumAdi(d)} ${v.sayac[d]}`}</Etiket>)}
          <button type="button" disabled={mesgul} aria-busy={mesgul || undefined} onClick={() => void indir()} className="ml-auto h-kontrol-kucuk shrink-0 rounded-full border px-3 text-sm font-medium transition border-line bg-surface text-ink-2 hover:border-line-strong">
            {mesgul ? cevir("preparing...") : cevir("Export list (Excel)")}
          </button>
        </div>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-ink-3">
                <th className={TABLO_BASLIGI}>{cevir("No.")}</th>
                <th className={TABLO_BASLIGI}>{cevir("List item")}</th>
                <th className={TABLO_BASLIGI}>{cevir("Documents")}</th>
                <th className="py-1 font-semibold">{cevir("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {v.bolumler.map((b) => (
                <Fragment key={b.no}>
                  <tr className="border-b border-line">
                    <td colSpan={4} className="bg-surface-2 px-2 py-1 text-xs font-semibold text-ink-2">{listeBolumBasligi(b)}</td>
                  </tr>
                  {v.liste.filter((k) => k.bolum === b.no).map((k) => (
                    <tr key={k.no} className="border-b border-line">
                      <td className="py-2 pr-3 font-mono text-xs text-ink-3">{k.no}</td>
                      <td className={TABLO_HUCRESI}><b>{k.ad}</b></td>
                      <td className="py-2 pr-3 text-ink-2">
                        {listeParcalari(k, o.dizin, BICIM, o.isEmri).map((p, i) => (
                          <Fragment key={i}>
                            {i > 0 && " · "}
                            {p.satir ? <Baglanti a={acilis(p.satir)} sayfa={p.satir.sayfa} metin={p.metin} goster={o.goster} /> : p.metin}
                          </Fragment>
                        ))}
                      </td>
                      <td className="py-2"><Etiket tone={DURUM_TONU[k.durum]}>{durumAdi(k.durum)}</Etiket></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Kutu>
      {!o.bantGizli && <Band tip="bilgi">{cevir("The list follows the contents pages of the Oil States overhaul record book: 5 sections, 30 items. It is built from the documents on file; nothing is typed here. “Not applicable” is set by the riser's scope (no pressure test on the main tube, no section 4 on a riser without weld repair). Compiling the file and publishing belong to “MRB package / DNV”; the signed certificate and the DNV report are uploaded on Project documents.")}</Band>}
    </>
  );
}
